import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MdArrowBack, MdCameraAlt, MdLock, MdVisibility, MdVisibilityOff } from "react-icons/md";
import api from "../services/api";
import { useAuth } from "../context/AuthContext";
import toast from "react-hot-toast";

const COUNTRY_CODES = [
  { code: "+1", label: "United States (+1)" },
  { code: "+44", label: "United Kingdom (+44)" },
  { code: "+61", label: "Australia (+61)" },
  { code: "+49", label: "Germany (+49)" },
  { code: "+33", label: "France (+33)" },
  { code: "+971", label: "United Arab Emirates (+971)" },
  { code: "+966", label: "Saudi Arabia (+966)" },
  { code: "+92", label: "Pakistan (+92)" },
  { code: "+91", label: "India (+91)" },
  { code: "+880", label: "Bangladesh (+880)" },
  { code: "+94", label: "Sri Lanka (+94)" },
  { code: "+93", label: "Afghanistan (+93)" },
  { code: "+81", label: "Japan (+81)" },
  { code: "+86", label: "China (+86)" },
  { code: "+60", label: "Malaysia (+60)" },
  { code: "+62", label: "Indonesia (+62)" },
  { code: "+63", label: "Philippines (+63)" },
  { code: "+65", label: "Singapore (+65)" },
  { code: "+27", label: "South Africa (+27)" },
  { code: "+234", label: "Nigeria (+234)" },
  { code: "+254", label: "Kenya (+254)" },
  { code: "+255", label: "Tanzania (+255)" },
  { code: "+20", label: "Egypt (+20)" },
  { code: "+34", label: "Spain (+34)" },
  { code: "+39", label: "Italy (+39)" },
  { code: "+7", label: "Russia (+7)" },
  { code: "+55", label: "Brazil (+55)" },
  { code: "+52", label: "Mexico (+52)" },
  { code: "+90", label: "Turkey (+90)" },
  { code: "+353", label: "Ireland (+353)" },
  { code: "+46", label: "Sweden (+46)" },
  { code: "+47", label: "Norway (+47)" },
  { code: "+45", label: "Denmark (+45)" },
  { code: "+31", label: "Netherlands (+31)" },
  { code: "+41", label: "Switzerland (+41)" },
  { code: "+48", label: "Poland (+48)" },
  { code: "+420", label: "Czech Republic (+420)" },
  { code: "+43", label: "Austria (+43)" },
];

const API_URL =
  process.env.NODE_ENV === "production"
    ? process.env.REACT_APP_API_URL || window.location.origin
    : "http://localhost:6000";

const parsePhone = (phone) => {
  if (!phone) {
    return { countryCode: "+92", local: "" };
  }
  const trimmed = String(phone).trim();
  const match = trimmed.match(/^(\+\d{1,4})(.*)$/);
  if (match) {
    return {
      countryCode: match[1],
      local: match[2].replace(/^\s+/, "").replace(/^0+/, ""),
    };
  }
  return { countryCode: "+92", local: trimmed.replace(/^0+/, "") };
};

const managersFromUser = (u) => {
  if (!u) return [];
  const fromManagers = (u.managers || [])
    .map((m) => (m && m._id ? m._id : m))
    .filter(Boolean);
  const fromLegacy = u.managedBy ? [u.managedBy._id || u.managedBy] : [];
  return [...new Set([...fromManagers, ...fromLegacy])];
};

const normRole = (r) => (r === "user" ? "staff" : r || "staff");

const getStrength = (pwd) => {
  if (!pwd) return 0;
  let s = 0;
  if (pwd.length >= 6) s++;
  if (pwd.length >= 10) s++;
  if (/[A-Z]/.test(pwd)) s++;
  if (/[0-9]/.test(pwd)) s++;
  if (/[^A-Za-z0-9]/.test(pwd)) s++;
  return s;
};
const STRENGTH_LABEL = ["", "Very weak", "Weak", "Fair", "Strong", "Very strong"];
const STRENGTH_COLOR = ["", "#ef4444", "#f97316", "#eab308", "#22c55e", "#16a34a"];

const PasswordField = ({ label, value, onChange, required }) => {
  const [show, setShow] = useState(false);
  const strength = getStrength(value);
  return (
    <div className="form-group">
      <label className="form-label" style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <MdLock size={14} color="var(--primary)" /> {label}
      </label>
      <div style={{ position: "relative" }}>
        <input
          className="form-control"
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Min 6 characters"
          minLength={required ? 6 : 0}
          required={required}
          style={{ paddingRight: 40 }}
        />
        <button
          type="button"
          onClick={() => setShow((v) => !v)}
          style={{
            position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
            background: "none", border: "none", cursor: "pointer", padding: 0,
            color: "var(--text-muted)", display: "flex", alignItems: "center",
          }}
          aria-label={show ? "Hide password" : "Show password"}
        >
          {show ? <MdVisibilityOff size={18} /> : <MdVisibility size={18} />}
        </button>
      </div>
      {value && (
        <div style={{ marginTop: 6 }}>
          <div style={{ display: "flex", gap: 3, marginBottom: 3 }}>
            {[1, 2, 3, 4, 5].map((i) => (
              <div
                key={i}
                style={{
                  flex: 1, height: 3, borderRadius: 2,
                  background: i <= strength ? STRENGTH_COLOR[strength] : "var(--border)",
                  transition: "background .2s",
                }}
              />
            ))}
          </div>
          <span style={{ fontSize: "0.72rem", color: STRENGTH_COLOR[strength], fontWeight: 600 }}>
            {STRENGTH_LABEL[strength]}
          </span>
        </div>
      )}
    </div>
  );
};

const AdminStaffCreate = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const currentRole = currentUser?.role === "user" ? "staff" : currentUser?.role;
  const isSuperAdmin = currentRole === "super_admin";
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [organizations, setOrganizations] = useState([]);
  const [adminsList, setAdminsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);
  const fileRef = useRef(null);

  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    role: "staff",
    managers: [],
    isActive: true,
    organization: "",
    designation: "",
    phone: "",
    phoneCountryCode: "+92",
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const requests = [
          api.get("/organizations"),
          api.get("/users"),
        ];
        if (isEdit) {
          requests.push(api.get(`/users/${id}`));
        }
        const [orgRes, usersRes, userRes] = await Promise.all(requests);
        setOrganizations(orgRes.data.data || []);
        const admins = (usersRes.data.data || []).filter((u) =>
          ["admin", "super_admin", "accounts", "hr"].includes(u.role),
        );
        setAdminsList(admins);

        if (isEdit && userRes) {
          const u = userRes.data.data;
          const phoneParsed = parsePhone(u.phone);
          setForm({
            name: u.name || "",
            email: u.email || "",
            password: "",
            role: normRole(u.role),
            managers: managersFromUser(u),
            isActive: u.isActive,
            organization: u.organization?._id || "",
            designation: u.designation || "",
            phone: phoneParsed.local,
            phoneCountryCode: phoneParsed.countryCode,
          });
          if (u.avatar) {
            setAvatarPreview(
              u.avatar.startsWith("http") ? u.avatar : `${API_URL}${u.avatar}`,
            );
          }
        }
      } catch (err) {
        toast.error("Failed to load data");
      } finally {
        setInitialLoading(false);
      }
    };
    fetchData();
  }, [id, isEdit]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const basePayload = {
        name: form.name,
        email: form.email,
        role: form.role,
        isActive: form.isActive,
        organization: form.organization || null,
        designation: form.designation,
        phone: `${form.phoneCountryCode || "+92"}${form.phone || ""}`,
      };

      if (isEdit) {
        const payload = { ...basePayload };
        if (form.role === "staff") {
          payload.managers = form.managers || [];
        } else {
          payload.managers = [];
        }

        const targetId = id;
        if (isSuperAdmin && form.password && String(form.password).trim().length >= 6) {
          payload.password = String(form.password).trim();
        }
        const { data: putRes } = await api.put(`/users/${targetId}`, payload);

        if (avatarFile) {
          const fd = new FormData();
          fd.append("avatar", avatarFile);
          fd.append("userId", targetId);
          try {
            await api.post("/users/avatar", fd, {
              headers: { "Content-Type": "multipart/form-data" },
            });
          } catch (uploadErr) {
            // eslint-disable-next-line no-console
            console.error("Avatar upload failed", uploadErr);
          }
        }

        let msg = "User updated";
        if (typeof putRes.tasksReassignedToManager === "number" && form.role === "staff") {
          if (putRes.tasksReassignedToManager > 0) {
            msg = `User updated. ${putRes.tasksReassignedToManager} task(s) moved to their manager.`;
          } else if (putRes.taskReassignmentSkippedReason === "no_tagged_manager") {
            msg =
              "User updated. No tagged manager — tasks were not reassigned.";
          }
        }
        toast.success(msg);
      } else {
        const payload = { ...basePayload };
      if (form.role === "staff") {
        const canAssignManagers = ["super_admin", "admin", "accounts", "hr"].includes(currentUser?.role);
        payload.managers = canAssignManagers
          ? form.managers || []
          : [currentUser?._id].filter(Boolean);
      } else {
          payload.managers = [];
        }

        const { data } = await api.post("/auth/register", {
          name: form.name,
          email: form.email,
          password: form.password,
        });
        const createdUserId = data.user._id;

        await api.put(`/users/${createdUserId}`, payload);

        if (avatarFile) {
          const fd = new FormData();
          fd.append("avatar", avatarFile);
          fd.append("userId", createdUserId);
          try {
            await api.post("/users/avatar", fd, {
              headers: { "Content-Type": "multipart/form-data" },
            });
          } catch (uploadErr) {
            // eslint-disable-next-line no-console
            console.error("Avatar upload failed", uploadErr);
          }
        }

        toast.success("User added");
      }
      navigate("/admin/staff");
    } catch (err) {
      toast.error(err.response?.data?.message || "Error saving");
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <main className="page">
        <div className="loading">
          <div className="spinner" />
        </div>
      </main>
    );
  }

  return (
    <main className="page">
      <div className="page-header">
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => navigate("/admin/staff")}
          >
            <MdArrowBack size={18} />
            Back
          </button>
          <div>
            <h1 className="page-title">
              {isEdit ? "Edit Staff Member" : "Add Staff Member"}
            </h1>
            <p className="page-subtitle">
              {isEdit
                ? "Update staff details, profile photo, organization and role."
                : "Create a new staff account, upload a profile photo, and assign organization, role and contact details."}
            </p>
          </div>
        </div>
      </div>

      <div className="card" style={{ padding: 22, maxWidth: "100%" }}>
        <form onSubmit={handleSubmit}>
          {/* Avatar selector */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 18,
              marginBottom: 22,
              flexWrap: "wrap",
            }}
          >
            <div style={{ position: "relative", width: 72, height: 72 }}>
              <div
                className="avatar avatar-lg"
                style={{
                  width: 72,
                  height: 72,
                  fontSize: "1.3rem",
                  background: avatarPreview ? "transparent" : "#6366f1",
                }}
              >
                {avatarPreview ? (
                  <img
                    src={avatarPreview}
                    alt="Staff avatar preview"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      borderRadius: "50%",
                    }}
                    onError={() => setAvatarPreview(null)}
                  />
                ) : (
                  "ST"
                )}
              </div>
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                style={{
                  position: "absolute",
                  bottom: -2,
                  right: -2,
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  background: "var(--primary)",
                  border: "2px solid #fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <MdCameraAlt size={14} color="#fff" />
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  setAvatarFile(file);
                  setAvatarPreview(URL.createObjectURL(file));
                }}
              />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: "0.95rem" }}>
                Staff profile photo
              </div>
              <div className="text-muted" style={{ fontSize: "0.8rem" }}>
                Optional – JPG or PNG, up to a few MB. Click the camera icon to
                upload.
              </div>
            </div>
          </div>

          <div className="grid grid-2">
            <div className="form-group">
              <label className="form-label">Full Name</label>
              <input
                className="form-control"
                value={form.name}
                onChange={(e) =>
                  setForm((f) => ({ ...f, name: e.target.value }))
                }
                placeholder="John Doe"
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input
                className="form-control"
                type="email"
                value={form.email}
                onChange={(e) =>
                  setForm((f) => ({ ...f, email: e.target.value }))
                }
                placeholder="john@example.com"
                required
              />
            </div>
          </div>

          {!isEdit && (
            <PasswordField
              label="Password *"
              value={form.password}
              onChange={(v) => setForm((f) => ({ ...f, password: v }))}
              required
            />
          )}

          {isEdit && isSuperAdmin && (
            <div
              style={{
                background: "rgba(99,102,241,0.06)",
                border: "1px solid rgba(99,102,241,0.2)",
                borderRadius: 10,
                padding: "14px 16px",
                marginBottom: 16,
              }}
            >
              <div
                style={{
                  fontWeight: 700,
                  fontSize: "0.78rem",
                  textTransform: "uppercase",
                  letterSpacing: ".5px",
                  color: "var(--primary)",
                  marginBottom: 10,
                }}
              >
                Super admin — reset password
              </div>
              <PasswordField
                label="New password (leave blank to keep current)"
                value={form.password}
                onChange={(v) => setForm((f) => ({ ...f, password: v }))}
                required={false}
              />
              <p className="text-muted" style={{ fontSize: "0.78rem", margin: "8px 0 0" }}>
                Only super admins see this. Minimum 6 characters when setting a new password.
              </p>
            </div>
          )}

          <div
            style={{
              background: "var(--surface2)",
              borderRadius: 10,
              padding: "14px 16px",
              marginBottom: 16,
            }}
          >
            <div
              style={{
                fontWeight: 700,
                fontSize: "0.78rem",
                textTransform: "uppercase",
                letterSpacing: ".5px",
                color: "var(--text-muted)",
                marginBottom: 12,
              }}
            >
              Organization Info
            </div>
            <div className="form-group">
              <label className="form-label">Organization</label>
              <select
                className="form-control"
                value={form.organization}
                onChange={(e) =>
                  setForm((f) => ({ ...f, organization: e.target.value }))
                }
              >
                <option value="">— No Organization —</option>
                {organizations.map((o) => (
                  <option key={o._id} value={o._id}>
                    {o.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="grid grid-2">
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Designation</label>
                <input
                  className="form-control"
                  value={form.designation}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, designation: e.target.value }))
                  }
                  placeholder="e.g. Manager"
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Phone</label>
                <div style={{ display: "flex", gap: 8 }}>
                  <select
                    className="form-control"
                    style={{ maxWidth: 150 }}
                    value={form.phoneCountryCode}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        phoneCountryCode: e.target.value,
                      }))
                    }
                  >
                    {COUNTRY_CODES.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                  <input
                    className="form-control"
                    value={form.phone}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, phone: e.target.value }))
                    }
                    placeholder="3001234567"
                  />
                </div>
                <div
                  className="text-muted"
                  style={{ fontSize: "0.7rem", marginTop: 4 }}
                >
                  Select country code and enter number without leading 0.
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-2">
            <div className="form-group">
              <label className="form-label">System Role</label>
              <select
                className="form-control"
                value={form.role}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    role: e.target.value,
                    managers: e.target.value === "staff" ? f.managers : [],
                  }))
                }
              >
                <option value="staff">Staff</option>
                <option value="admin">Admin</option>
                <option value="accounts">Accounts</option>
                <option value="hr">HR</option>
                {currentUser?.role === "super_admin" && (
                  <option value="super_admin">Super Admin</option>
                )}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Status</label>
              <select
                className="form-control"
                value={String(form.isActive)}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    isActive: e.target.value === "true",
                  }))
                }
              >
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
          </div>

          {form.role === "staff" && (currentUser?.role === "super_admin" || currentUser?.role === "accounts" || currentUser?.role === "hr") && adminsList && adminsList.length > 0 && (
            <div className="form-group" style={{ marginTop: 8 }}>
              <label className="form-label">
                Managers (multiple admins can manage this staff)
              </label>
              <select
                className="form-control"
                multiple
                value={form.managers || []}
                onChange={(e) => {
                  const selected = Array.from(
                    e.target.selectedOptions,
                    (o) => o.value,
                  );
                  setForm((f) => ({ ...f, managers: selected }));
                }}
                style={{ minHeight: 100 }}
              >
                {adminsList.map((a) => (
                  <option key={a._id} value={a._id}>
                    {a.name}{" "}
                    {a.role === "super_admin"
                      ? "(Super Admin)"
                      : a.role === "admin"
                        ? "(Admin)"
                        : a.role === "accounts"
                          ? "(Accounts)"
                          : "(HR)"}
                  </option>
                ))}
              </select>
              <div
                className="text-muted"
                style={{ fontSize: "0.75rem", marginTop: 4 }}
              >
                Hold Ctrl/Cmd to select multiple.
              </div>
            </div>
          )}

          <div
            className="modal-footer"
            style={{ padding: "0", marginTop: 16, justifyContent: "flex-end" }}
          >
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate("/admin/staff")}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading
                ? "Saving..."
                : isEdit
                  ? "Update Staff"
                  : "Create Staff"}
            </button>
          </div>
        </form>
      </div>
    </main>
  );
};

export default AdminStaffCreate;
